import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../../components/SEO/PageMeta';
import SyntaxHighlightedCode from '../../components/SyntaxHighlightedCode/SyntaxHighlightedCode';
import { insertIndent, insertSmartNewLine } from '../../utils/codeEditing';
import styles from './JavaScriptTerminalPage.module.css';

const HISTORY_KEY = 'saveitpro-js-terminal-history';
const THEME_KEY = 'saveitpro-js-terminal-theme';
const MAX_HISTORY = 100;

type TerminalEntryType = 'command' | 'result' | 'error' | 'system';

interface TerminalEntry {
  id: number;
  type: TerminalEntryType;
  text: string;
}

interface TerminalTheme {
  background: string;
  text: string;
}

const DEFAULT_THEME: TerminalTheme = {
  background: '#0f172a',
  text: '#e2e8f0',
};

const HELP_TEXT = `Commands:
  help        Show this help
  clear       Clear the terminal
  history     Show saved command history

Shortcuts:
  Enter           Run current code
  Shift+Enter     Add a smart-indented new line
  Cmd/Ctrl+Enter  Run current code
  Tab             Complete from history, or insert indentation
  ↑ / ↓           Navigate command history

This terminal runs JavaScript in the browser sandbox. It cannot access your local filesystem,
installed system libraries, shell commands, Node.js packages, or private machine resources.`;

export default function JavaScriptTerminalPage() {
  const contextRef = useRef<Record<string, unknown>>({});
  const outputRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<string[]>(() => loadHistory());
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const [tabMatchIndex, setTabMatchIndex] = useState(0);
  const [inputScrollTop, setInputScrollTop] = useState(0);
  const [inputScrollLeft, setInputScrollLeft] = useState(0);
  const [theme, setTheme] = useState<TerminalTheme>(() => loadTheme());
  const [entries, setEntries] = useState<TerminalEntry[]>([
    {
      id: 1,
      type: 'system',
      text: 'Browser JavaScript terminal ready. Type help, then press Enter.',
    },
  ]);

  const terminalStyle = useMemo(
    () => ({
      backgroundColor: theme.background,
      color: theme.text,
    }),
    [theme.background, theme.text],
  );

  useEffect(() => {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    window.localStorage.setItem(THEME_KEY, JSON.stringify(theme));
  }, [theme]);

  useEffect(() => {
    outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight });
  }, [entries]);

  async function executeCommand(rawCommand: string) {
    const command = rawCommand.trim();
    if (!command) {
      return;
    }

    setEntries((current) => [...current, makeEntry('command', `> ${command}`)]);
    setInput('');
    setHistoryIndex(null);
    setTabMatchIndex(0);
    rememberCommand(command);

    if (command === 'clear') {
      setEntries([]);
      return;
    }

    if (command === 'help') {
      setEntries((current) => [...current, makeEntry('system', HELP_TEXT)]);
      return;
    }

    if (command === 'history') {
      setEntries((current) => [
        ...current,
        makeEntry(
          'system',
          history.length
            ? history.map((item, index) => `${index + 1}. ${item}`).join('\n')
            : 'No history yet.',
        ),
      ]);
      return;
    }

    try {
      const logs: string[] = [];
      const result = await evaluateJavaScript(command, contextRef.current, logs);
      setEntries((current) => {
        const nextEntries = [...current, ...logs.map((log) => makeEntry('result', log))];
        if (result !== undefined) {
          nextEntries.push(makeEntry('result', formatResult(result)));
        }

        return nextEntries;
      });
    } catch (error) {
      setEntries((current) => [
        ...current,
        makeEntry('error', error instanceof Error ? error.message : String(error)),
      ]);
    }
  }

  function rememberCommand(command: string) {
    setHistory((current) => {
      const next = [command, ...current.filter((item) => item !== command)].slice(0, MAX_HISTORY);
      return next;
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      applyInputEdit(insertSmartNewLine);
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void executeCommand(input);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (history.length === 0) return;
      const nextIndex = historyIndex === null ? 0 : Math.min(historyIndex + 1, history.length - 1);
      setHistoryIndex(nextIndex);
      setInput(history[nextIndex] ?? '');
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (historyIndex === null) return;
      const nextIndex = historyIndex - 1;
      if (nextIndex < 0) {
        setHistoryIndex(null);
        setInput('');
      } else {
        setHistoryIndex(nextIndex);
        setInput(history[nextIndex] ?? '');
      }
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      const query = input.trim();
      const matches = history.filter((item) => item.startsWith(query));
      if (matches.length === 0) {
        applyInputEdit(insertIndent);
        return;
      }
      const next = matches[tabMatchIndex % matches.length];
      setInput(next);
      setTabMatchIndex((current) => current + 1);
    }
  }

  function applyInputEdit(
    edit: (value: string, start: number, end: number) => { value: string; cursor: number },
  ) {
    const start = inputRef.current?.selectionStart ?? input.length;
    const end = inputRef.current?.selectionEnd ?? start;
    const next = edit(input, start, end);

    setInput(next.value);
    setTabMatchIndex(0);
    window.requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.selectionStart = next.cursor;
      inputRef.current.selectionEnd = next.cursor;
      inputRef.current.focus();
    });
  }

  return (
    <>
      <PageMeta
        title="Online JavaScript Terminal"
        description="Run JavaScript snippets in a browser-sandboxed online terminal with command history, Tab completion, and customizable terminal colors."
        path="/javascript-terminal"
      />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden> / </span>
        <span>JavaScript Terminal</span>
      </nav>

      <section className={styles.hero}>
        <p className={styles.eyebrow}>Browser sandbox</p>
        <h1>Online JavaScript Terminal</h1>
        <p>
          Run JavaScript snippets directly in your browser, reuse past commands with Tab or arrow
          keys, and customize the terminal colors. This tool cannot access local system libraries or
          shell commands from a hosted website.
        </p>
      </section>

      <section className={styles.terminalShell} aria-label="Online JavaScript terminal">
        <div className={styles.toolbar}>
          <div className={styles.colorControls}>
            <label>
              Background
              <input
                type="color"
                value={theme.background}
                onChange={(event) =>
                  setTheme((current) => ({ ...current, background: event.target.value }))
                }
              />
            </label>
            <label>
              Text
              <input
                type="color"
                value={theme.text}
                onChange={(event) =>
                  setTheme((current) => ({ ...current, text: event.target.value }))
                }
              />
            </label>
          </div>
          <div className={styles.toolbarActions}>
            <button type="button" className={styles.button} onClick={() => setTheme(DEFAULT_THEME)}>
              Reset colors
            </button>
            <button type="button" className={styles.button} onClick={() => setEntries([])}>
              Clear output
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                setHistory([]);
                setHistoryIndex(null);
              }}
            >
              Clear history
            </button>
          </div>
        </div>

        <div className={styles.terminal} style={terminalStyle}>
          <div ref={outputRef} className={styles.output} aria-live="polite">
            {entries.map((entry) => (
              <div key={entry.id} className={`${styles.line} ${styles[`${entry.type}Line`]}`}>
                {renderEntry(entry)}
              </div>
            ))}
          </div>
          <label className={styles.promptRow}>
            <span className={styles.prompt}>js&gt;</span>
            <div className={styles.promptInputWrap}>
              <div className={styles.inputFrame}>
                <div className={styles.inputHighlight} aria-hidden>
                  <div
                    style={{
                      transform: `translate(${-inputScrollLeft}px, ${-inputScrollTop}px)`,
                    }}
                  >
                    {input ? (
                      <SyntaxHighlightedCode code={input} />
                    ) : (
                      <span className={styles.placeholder}>{`const users = ["Ada", "Linus"];
users.map((name) => name.toUpperCase())`}</span>
                    )}
                  </div>
                </div>
                <textarea
                  ref={inputRef}
                  className={styles.input}
                  style={{ caretColor: theme.text }}
                  value={input}
                  onChange={(event) => {
                    setInput(event.target.value);
                    setTabMatchIndex(0);
                  }}
                  onScroll={(event) => {
                    setInputScrollTop(event.currentTarget.scrollTop);
                    setInputScrollLeft(event.currentTarget.scrollLeft);
                  }}
                  onKeyDown={handleKeyDown}
                  spellCheck={false}
                  autoComplete="off"
                  rows={4}
                  placeholder={`const users = ["Ada", "Linus"];
users.map((name) => name.toUpperCase())`}
                  aria-label="JavaScript command"
                />
              </div>
              <span className={styles.promptActions}>
                <span>
                  Enter to run · Shift+Enter for smart new line · Tab to complete or indent
                </span>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => void executeCommand(input)}
                >
                  Run
                </button>
              </span>
            </div>
          </label>
        </div>
      </section>

      <section className={styles.hintGrid} aria-label="Terminal notes">
        <article className={styles.hintCard}>
          <h2>History memory</h2>
          <p>
            Commands are saved in this browser. Press Tab to complete from matching past commands.
          </p>
        </article>
        <article className={styles.hintCard}>
          <h2>Browser APIs</h2>
          <p>
            Use browser-safe APIs such as Math, Date, JSON, URL, and fetch where allowed by CORS.
          </p>
        </article>
        <article className={styles.hintCard}>
          <h2>No local shell</h2>
          <p>Hosted websites cannot run your installed system commands or local Node packages.</p>
        </article>
      </section>
    </>
  );
}

function renderEntry(entry: TerminalEntry) {
  if (entry.type === 'command' && entry.text.startsWith('> ')) {
    return (
      <>
        <span className={styles.commandPrompt}>&gt; </span>
        <SyntaxHighlightedCode code={entry.text.slice(2)} className={styles.inlineCode} />
      </>
    );
  }

  if (entry.type === 'result') {
    return <SyntaxHighlightedCode code={entry.text} className={styles.inlineCode} />;
  }

  return <pre>{entry.text}</pre>;
}

async function evaluateJavaScript(
  command: string,
  context: Record<string, unknown>,
  logs: string[],
): Promise<unknown> {
  const sandbox = createSandbox(context, logs);
  const expressionResult = await tryEvaluate(`return await (${command});`, sandbox);

  if (expressionResult.ok) {
    return expressionResult.value;
  }

  const finalExpressionBody = buildFinalExpressionBody(command);
  if (finalExpressionBody) {
    const finalExpressionResult = await tryEvaluate(finalExpressionBody, sandbox);
    if (finalExpressionResult.ok) {
      return finalExpressionResult.value;
    }
  }

  const statementResult = await tryEvaluate(command, sandbox);
  if (statementResult.ok) {
    return statementResult.value;
  }

  throw expressionResult.error ?? statementResult.error ?? new Error('Unable to evaluate command.');
}

function buildFinalExpressionBody(command: string): string | null {
  const lines = command.split('\n');
  const lastLineIndex = findLastMeaningfulLineIndex(lines);

  if (lastLineIndex < 0) {
    return null;
  }

  const lastLine = lines[lastLineIndex];
  const expression = lastLine.trim();

  if (
    !expression ||
    expression.endsWith(';') ||
    expression.endsWith('{') ||
    expression.endsWith('}')
  ) {
    return null;
  }

  if (
    /^(const|let|var|if|for|while|switch|try|catch|finally|class|function|return|throw|import|export)\b/.test(
      expression,
    )
  ) {
    return null;
  }

  const before = lines.slice(0, lastLineIndex).join('\n');
  return `${before}\nreturn await (${expression});`;
}

function findLastMeaningfulLineIndex(lines: string[]): number {
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    if (lines[index].trim()) {
      return index;
    }
  }

  return -1;
}

async function tryEvaluate(
  body: string,
  sandbox: Record<string | symbol, unknown>,
): Promise<{ ok: true; value: unknown } | { ok: false; error: Error }> {
  try {
    const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
    const runner = new AsyncFunction('sandbox', `with (sandbox) { ${body} }`);
    return { ok: true, value: await runner(sandbox) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error : new Error(String(error)) };
  }
}

function createSandbox(context: Record<string, unknown>, logs: string[]) {
  const consoleProxy = {
    log: (...args: unknown[]) => logs.push(args.map(formatResult).join(' ')),
    info: (...args: unknown[]) => logs.push(args.map(formatResult).join(' ')),
    warn: (...args: unknown[]) => logs.push(`Warning: ${args.map(formatResult).join(' ')}`),
    error: (...args: unknown[]) => logs.push(`Error: ${args.map(formatResult).join(' ')}`),
  };

  const builtIns: Record<string, unknown> = {
    Array,
    Boolean,
    Date,
    Error,
    fetch,
    isFinite,
    isNaN,
    JSON,
    Map,
    Math,
    Number,
    Object,
    parseFloat,
    parseInt,
    Promise,
    RegExp,
    Set,
    String,
    URL,
    URLSearchParams,
    console: consoleProxy,
  };

  return new Proxy(context, {
    has: () => true,
    get(target, key) {
      if (key === Symbol.unscopables) return undefined;
      if (typeof key === 'string' && key in target) return target[key];
      if (typeof key === 'string' && key in builtIns) return builtIns[key];
      return undefined;
    },
    set(target, key, value) {
      if (typeof key === 'string') {
        target[key] = value;
      }
      return true;
    },
  });
}

function formatResult(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'function') return value.toString();

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function makeEntry(type: TerminalEntryType, text: string): TerminalEntry {
  return {
    id: Date.now() + Math.random(),
    type,
    text,
  };
}

function loadHistory(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(HISTORY_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function loadTheme(): TerminalTheme {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(THEME_KEY) ?? '{}');
    if (typeof parsed?.background === 'string' && typeof parsed?.text === 'string') {
      return parsed;
    }
  } catch {
    // Fall through to default theme.
  }

  return DEFAULT_THEME;
}
